/**
 * A Connect Link's raw token in a URL path (`/_account/connect/<token>`, its
 * `%5F` and `%2F` spellings too), with the path kept so the event still shows
 * which route it came from.
 */
const TOKEN_IN_PATH = /((?:\/|%2F)(?:_|%5F)account(?:\/|%2F)connect(?:\/|%2F))[A-Za-z0-9_-]{43}(?![A-Za-z0-9_-])/gi;

/** What a scrubbed token reads as. */
export const REDACTED_CONNECT_LINK_TOKEN = '[token]';

/** `text` with every Connect Link token in a `/_account/connect/…` path replaced. */
export function scrubConnectLinkTokens(text: string): string {
  return text.replace(TOKEN_IN_PATH, `$1${REDACTED_CONNECT_LINK_TOKEN}`);
}

function scrubInPlace(value: unknown, seen: WeakSet<object>): void {
  if (typeof value !== 'object' || value === null || seen.has(value)) return;
  seen.add(value);
  for (const [key, item] of Object.entries(value)) {
    if (typeof item === 'string') {
      const scrubbed = scrubConnectLinkTokens(item);
      if (scrubbed !== item) Reflect.set(value, key, scrubbed);
    } else {
      scrubInPlace(item, seen);
    }
  }
}

/**
 * Sentry's `beforeSend` for every runtime (`sentry.server.config.ts`,
 * `sentry.edge.config.ts`, `instrumentation-client.ts`): replaces Connect Link
 * tokens in every string anywhere in the event, in place, and returns it.
 *
 * A Connect Link's URL is the whole authority to replace a tenant's
 * Connections for 7 days (ADR 0003), and Sentry would otherwise store it: the
 * server records the request path of every failed render
 * (`contexts.nextjs.request_path`, and the link page deliberately lets a
 * database failure throw), and the browser client records `location.href` on
 * every error. Walking the whole event, not a list of fields, also catches
 * breadcrumbs, headers (`Referer`), exception messages and anything Sentry
 * adds later. Cycles are skipped.
 *
 * Kept free of `env` and of Node built-ins: the browser bundle imports it.
 */
export function scrubConnectLinkTokensFromEvent<Event>(event: Event): Event {
  scrubInPlace(event, new WeakSet());
  return event;
}
